import {
  DATE_FORMAT_WEEK_DAY_FNS,
  DEFAULT_DATE_FORMAT_FNS,
  useGetFacilitiesQuery,
  useGetStaffScheduleQuery,
  useGetTimetableStructuresQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useWeeklyScheduleGrid } from '../../../../hooks/useWeeklyScheduleGrid';
import { useAttendanceCountsPoints } from '../Groups/useAttendanceCountsPoints';
import { ASSIGNABLE_PERIODS } from '../../../School/SchoolTimetables/CreateTimetable/TimetableSlotCard';
import { addScheduleEndAdornments } from '../../../ProfileModal/tabs/ProfileModalSchedule/ProfileModalSchedule';
import {
  buildRoomMap,
  extendTimeRange,
  getScheduleTimeRange,
} from '../../../ProfileModal/tabs/ProfileModalSchedule/utils';

const MIN_VISIBLE_HOURS = 8;

interface UseTimetableWidgetDataParams {
  selectedDay: Date;
  canViewAttendance: boolean;
}

export const useTimetableWidgetData = ({
  selectedDay,
  canViewAttendance,
}: UseTimetableWidgetDataParams) => {
  const navigate = useNavigate();
  const { schoolId = '', relationId = '' } = useAuth();

  const onGroupClick = useCallback(
    (groupId: string) => {
      navigate(`/groups/${groupId}`);
    },
    [navigate],
  );

  const structuresQueryEnabled = !!schoolId;

  const { data: structuresData, isLoading: isLoadingStructures } = useGetTimetableStructuresQuery(
    { school_id: schoolId },
    { enabled: structuresQueryEnabled },
  );

  const schoolHasStructures = !!structuresData?.results?.length;
  const scheduleQueryEnabled = !!relationId && schoolHasStructures;
  const facilitiesQueryEnabled = !!schoolId && schoolHasStructures;

  const { data: scheduleData, isLoading: isScheduleLoading } = useGetStaffScheduleQuery(
    { staffId: relationId },
    { enabled: scheduleQueryEnabled, refetchOnMount: 'always' },
  );

  const { data: facilitiesData, isLoading: isFacilitiesLoading } = useGetFacilitiesQuery(
    { schoolId },
    { enabled: facilitiesQueryEnabled },
  );

  const loadingStructures = structuresQueryEnabled && isLoadingStructures;
  const loadingSchedule = scheduleQueryEnabled && isScheduleLoading;
  const loadingFacilities = facilitiesQueryEnabled && isFacilitiesLoading;
  const isLoading = loadingStructures || loadingSchedule || loadingFacilities;

  const roomMap = useMemo(() => buildRoomMap(facilitiesData?.facilities ?? []), [facilitiesData]);

  const getTimeRange = useCallback(
    (periods: Parameters<typeof getScheduleTimeRange>[0]) =>
      extendTimeRange(getScheduleTimeRange(periods), MIN_VISIBLE_HOURS),
    [],
  );

  const { structures, selectedStructure, setSelectedStructureId, items, timeRange } =
    useWeeklyScheduleGrid({
      schedule: scheduleData,
      roomMap,
      onLabelClick: onGroupClick,
      transformOptions: { allowedPeriodTypes: ASSIGNABLE_PERIODS },
      getTimeRange,
    });

  const itemsWithAdornments = useMemo(
    () =>
      addScheduleEndAdornments(
        items.map((item) => ({ ...item, data: { ...item.data, staffLabel: undefined } })),
      ),
    [items],
  );

  const selectedWeekday = format(selectedDay, DATE_FORMAT_WEEK_DAY_FNS);
  const selectedDate = format(selectedDay, DEFAULT_DATE_FORMAT_FNS);

  const dayItems = useMemo(
    () => itemsWithAdornments.filter((item) => item.day === selectedWeekday),
    [itemsWithAdornments, selectedWeekday],
  );

  const groupIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of dayItems) {
      for (const group of item.data.groupsWithRoom) ids.add(group.id);
    }
    return Array.from(ids);
  }, [dayItems]);

  const { pointsByGroupId } = useAttendanceCountsPoints({
    schoolId,
    date: selectedDate,
    groupIds,
    enabled: canViewAttendance,
  });

  return {
    structures,
    selectedStructure,
    setSelectedStructureId,
    dayItems,
    pointsByGroupId,
    timeRange,
    isLoading,
    hasStructures: schoolHasStructures,
  };
};
